import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Image,
  Newspaper,
  MessageSquare,
  ShoppingBag,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const navItems = [
  { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
  { name: "Users", path: "/admin/users", icon: Users },
  { name: "Media Gallery", path: "/admin/media", icon: Image },
  { name: "Latest Updates", path: "/admin/news", icon: Newspaper },
  { name: "Support Messages", path: "/admin/support", icon: MessageSquare },
  { name: "Store Manager", path: "/admin/store", icon: ShoppingBag },
  { name: "Page Content", path: "/admin/content", icon: Settings },
];

export default function AdminLayout() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const userStr = localStorage.getItem("ka_admin_user");
    if (userStr) {
      try {
        setAdminUser(JSON.parse(userStr));
      } catch (e) {
        navigate("/admin/login");
      }
    } else {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("ka_admin_token");
    localStorage.removeItem("ka_admin_user");
    navigate("/admin/login");
  };

  return (
    <div className="flex h-screen bg-[#0a0a0a] text-white">
      {/* Sidebar */}
      <aside 
        className={cn(
          "relative flex flex-col border-r border-[#8B0000]/30 transition-all duration-300",
          isCollapsed ? "w-16" : "w-64"
        )}
      >
        <div className="p-4 flex items-center gap-3">
          <Shield className="w-8 h-8 text-[#8B0000] shrink-0" />
          {!isCollapsed && <span className="text-xl font-bold tracking-tighter">ADMIN PANEL</span>}
        </div>
        
        <ScrollArea className="flex-1 px-3 py-4">
          <div className="space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start gap-3 px-2 transition-all duration-200",
                      isActive ? "bg-[#8B0000]/20 text-white border-l-2 border-[#8B0000]" : "text-gray-400 hover:text-white hover:bg-white/5",
                      isCollapsed && "justify-center px-0"
                    )}
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    {!isCollapsed && <span>{item.name}</span>}
                  </Button>
                </Link>
              );
            })}
          </div>
        </ScrollArea>

        <Separator className="bg-[#8B0000]/20" />
        
        <div className="p-3">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={cn(
              "w-full justify-start gap-3 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/10",
              isCollapsed && "justify-center px-0"
            )}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Logout</span>}
          </Button>
        </div>

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-20 bg-[#8B0000] rounded-full p-1 border border-black"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 border-b border-[#8B0000]/30 flex items-center justify-between px-6 bg-[#0a0a0a]/50 backdrop-blur-sm">
          <h2 className="text-lg font-medium">
            {navItems.find(item => item.path === location.pathname)?.name || "Dashboard"}
          </h2>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium">{adminUser?.username}</div>
              <div className="text-xs text-gray-500">{adminUser?.email}</div>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#8B0000]/20 border border-[#8B0000]/40 flex items-center justify-center">
              {adminUser?.username?.[0]?.toUpperCase() || "A"}
            </div>
          </div>
        </header>

        <ScrollArea className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </ScrollArea>
      </main>
    </div>
  );
}
